import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FilterAltRounded as FilterAltRoundedIcon,
  LockOutlined as LockOutlinedIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { tienePermisosModulo, puedeEditarProgramaContabilidad } from 'lib/seguridad/permisosPorModulo';
import { useVersionContable } from './versionContable';
import { FP, PageHeader, StatCard, fpDataGridSx, fpInputSx } from 'components/financepro';
import { useProductosMaterialContext } from 'context';
import PrintListado from 'utils/PrintListado';
import AvancesSuplidor from './AvancesSuplidor';

const PROGRAMA = 'mantenimientoavancessuplidor';

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const toNumber = (value) => parseFloat(String(value ?? '').replace(/,/g, '')) || 0;

const opcionLabel = (s) => {
  if (!s) return '';
  const id = s.id != null ? String(s.id) : '';
  const d = (s.descrip || '').trim();
  const r = (s.rnc || '').trim();
  return `${id}-${d}-${r}`;
};

const formatearFecha = (fecha) => {
  if (!fecha) return '-';
  try {
    const d = new Date(fecha);
    return d.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return fecha;
  }
};

const formatFecha = (fecha) => {
  if (fecha == null || fecha === '') return '';
  if (fecha instanceof Date && !Number.isNaN(fecha.getTime())) {
    const d = String(fecha.getDate()).padStart(2, '0');
    const m = String(fecha.getMonth() + 1).padStart(2, '0');
    const y = fecha.getFullYear();
    return `${d}/${m}/${y}`;
  }
  const s = String(fecha).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const dmyDash = s.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (dmyDash) return `${dmyDash[1]}/${dmyDash[2]}/${dmyDash[3]}`;
  return s;
};

const fechaLocalHoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const primerDiaAno = () => {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
};

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const btnPrimarySx = {
  height: 38,
  bgcolor: FP.color.navy,
  color: '#FFFFFF',
  borderRadius: 1.4,
  textTransform: 'none',
  fontWeight: 700,
  boxShadow: 'none',
  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
};

const btnOutlinedSx = {
  height: 38,
  borderColor: FP.color.border,
  color: FP.color.textPrimary,
  bgcolor: '#FFFFFF',
  borderRadius: 1.4,
  textTransform: 'none',
  fontWeight: 700,
  '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
};

const MantenimientoAvancesSuplidor = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = tienePermisosModulo(roleDetalles, PROGRAMA);
  // La versión fiscal es solo de consulta: ni registrar ni anular.
  const { soloConsulta } = useVersionContable();
  const puedeModificar =
    !soloConsulta && puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA);
  const puedeRegistrar =
    puedeModificar || (!soloConsulta && puedeEditarProgramaContabilidad(roleDetalles, 'avancessuplidor'));
  const puedeOperarDb = Boolean(selectedCompany?.db_empresa);

  const [dialogoNuevo, setDialogoNuevo] = useState(false);
  const [suplidores, setSuplidores] = useState([]);
  const [suplidorSel, setSuplidorSel] = useState(null);
  const [cargandoSupl, setCargandoSupl] = useState(false);
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [fechaDesde, setFechaDesde] = useState(primerDiaAno);
  const [fechaHasta, setFechaHasta] = useState(fechaLocalHoy);

  const totalLista = useMemo(
    () => filas.reduce((sum, r) => sum + toNumber(r.valor), 0),
    [filas]
  );

  const cargarSuplidores = useCallback(async () => {
    if (!puedeLeer || !puedeOperarDb) return;
    setCargandoSupl(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listarsuplidores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudieron cargar suplidores');
        setSuplidores([]);
        return;
      }
      setSuplidores(data.suplidores || []);
    } catch {
      setSuplidores([]);
      showError('Error de conexión al cargar suplidores');
    } finally {
      setCargandoSupl(false);
    }
  }, [puedeLeer, puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, showError]);

  const buscar = useCallback(async () => {
    if (!puedeLeer || !puedeOperarDb) return;
    setCargando(true);
    try {
      const body = {
        id_empresa: selectedCompany,
        usuario: usuarioLog || '',
        password: claveLog || '',
        fecha_desde: (fechaDesde || '').trim(),
        fecha_hasta: (fechaHasta || '').trim(),
        limite: 2000,
      };
      if (suplidorSel?.id) body.suplidor_id = String(suplidorSel.id);
      const response = await fetch(`${servidor}/contabilidadbackend_listarmantenimientoavancessuplidor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'Error al cargar avances');
        setFilas([]);
        return;
      }
      setFilas(data.avances || []);
    } catch {
      setFilas([]);
      showError('Error de conexión al cargar avances');
    } finally {
      setCargando(false);
    }
  }, [
    puedeLeer,
    puedeOperarDb,
    servidor,
    selectedCompany,
    usuarioLog,
    claveLog,
    fechaDesde,
    fechaHasta,
    suplidorSel,
    showError,
  ]);

  useEffect(() => {
    if (puedeLeer && puedeOperarDb) {
      cargarSuplidores();
    }
  }, [puedeLeer, puedeOperarDb, cargarSuplidores]);

  useEffect(() => {
    if (puedeLeer && puedeOperarDb) {
      buscar();
    }
  }, [puedeLeer, puedeOperarDb, selectedCompany?.db_empresa]);

  const eliminarAvance = useCallback(async (row) => {
    if (!puedeModificar) {
      showError('No tiene permiso para modificar');
      return;
    }
    if (!row.puede_eliminar) {
      showError(
        'No se puede eliminar: si el avance está aplicado a una factura CxP, esa factura tiene pagos registrados (vplicado).'
      );
      return;
    }
    const ok = window.confirm(
      `¿Eliminar el avance id ${row.id} del suplidor ${row.nombre_suplidor || row.suplidor}? Esta acción no se puede deshacer.`
    );
    if (!ok) return;
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_eliminaravancesuplidor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          avance_id: row.id,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudo eliminar');
        return;
      }
      showSuccess(data.mensaje || 'Avance eliminado');
      await buscar();
    } catch {
      showError('Error de conexión al eliminar');
    }
  }, [
    puedeModificar,
    servidor,
    selectedCompany,
    usuarioLog,
    claveLog,
    showError,
    showSuccess,
    buscar,
  ]);

  const nombreEmpresa =
    selectedCompany?.name || selectedCompany?.empresa || nombreNegocio || 'Empresa';

  const subtituloPrint = useMemo(() => {
    const sup = suplidorSel ? opcionLabel(suplidorSel) : 'Todos los suplidores';
    const fd = fechaDesde ? `Desde ${formatearFecha(fechaDesde)}` : '';
    const fh = fechaHasta ? `Hasta ${formatearFecha(fechaHasta)}` : '';
    return `${sup} · ${fd} · ${fh} · ${filas.length} registro(s)`;
  }, [suplidorSel, fechaDesde, fechaHasta, filas.length]);

  const columnasPrint = [
    { id: 'id', label: 'Id doc.', align: 'center' },
    { id: 'suplidor', label: 'Suplidor', align: 'center' },
    { id: 'nombre_suplidor', label: 'Nombre' },
    { id: 'fecha', label: 'Fecha', align: 'center', render: (_, row) => formatearFecha(row.fecha) },
    { id: 'valor', label: 'Valor', align: 'right', render: (v) => `$${fmtMoney(v)}` },
    { id: 'banco', label: 'Forma pago', align: 'left' },
    { id: 'tipodepago', label: 'Tipo pago', align: 'center' },
    { id: 'ncf', label: 'NCF', align: 'center' },
    { id: 'referencia', label: 'Referencia', align: 'left' },
    { id: 'nfact', label: 'Fact. aplicación', align: 'center' },
  ];

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        width: 80,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'fecha',
        headerName: 'Fecha',
        width: 120,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value) => formatFecha(value),
      },
      {
        field: 'nombre_suplidor',
        headerName: 'Suplidor',
        flex: 1,
        minWidth: 180,
      },
      {
        field: 'referencia',
        headerName: 'Referencia',
        flex: 1.2,
        minWidth: 160,
      },
      {
        field: 'valor',
        headerName: 'Valor',
        type: 'number',
        width: 140,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => `$${fmtMoney(value)}`,
      },
      {
        field: 'banco',
        headerName: 'Forma pago',
        minWidth: 140,
        flex: 0.8,
      },
      {
        field: 'ncf',
        headerName: 'NCF',
        width: 140,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value) => value || '—',
      },
      {
        field: 'nfact',
        headerName: 'Fact. aplicación',
        width: 140,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value) => value || '—',
      },
      {
        field: 'acciones',
        headerName: 'Acciones',
        width: 90,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Tooltip
            title={params.row?.puede_eliminar ? 'Eliminar avance' : 'No se puede eliminar este avance'}
          >
            <span>
              <IconButton
                size="small"
                disabled={!puedeModificar || !params.row?.puede_eliminar}
                onClick={() => eliminarAvance(params.row)}
                sx={{
                  color: FP.color.danger,
                  '&:hover': { bgcolor: FP.color.dangerSoft },
                  '&.Mui-disabled': { color: FP.color.textMuted },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ),
      },
    ],
    [puedeModificar, eliminarAvance]
  );

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Mantenimiento de avances a suplidor"
          subtitle={nombreNegocio ? `${nombreNegocio} · Avances suplidor` : 'Avances suplidor'}
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card elevation={0} sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}>
            <LockOutlinedIcon sx={{ fontSize: 30, color: FP.color.danger, mb: 2 }} />
            <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: FP.color.textPrimary, mb: 1 }}>
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem' }}>
              No tiene permiso del programa «mantenimientoavancessuplidor» en roles.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  if (!puedeOperarDb) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Mantenimiento de avances a suplidor"
          subtitle={nombreNegocio ? `${nombreNegocio} · Avances suplidor` : 'Avances suplidor'}
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Alert severity="info">Seleccione una empresa con base de datos asignada.</Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Mantenimiento de avances a suplidor"
        subtitle={nombreNegocio ? `${nombreNegocio} · Avances suplidor` : 'Avances suplidor'}
        cta={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogoNuevo(true)}
              disabled={!puedeRegistrar || !usuarioLog}
              sx={btnPrimarySx}
            >
              Agregar
            </Button>
            <PrintListado
              title={nombreEmpresa}
              reportTitle="Listado — mantenimiento avances a suplidor"
              subtitle={subtituloPrint}
              columns={columnasPrint}
              data={filas}
              emptyMessage="No hay datos para imprimir"
              footer={`Total valor avances: $${fmtMoney(totalLista)}`}
              printButtonLabel="Imprimir listado"
              printButtonProps={{
                disabled: filas.length === 0,
                sx: btnPrimarySx,
              }}
            />
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6} md={6}>
            <StatCard
              label="Avances en lista"
              value={String(filas.length)}
              icon={ReceiptIcon}
              accent={FP.color.accentBlue}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <StatCard
              label="Total valor"
              value={`$${fmtMoney(totalLista)}`}
              icon={ReceiptIcon}
              accent={FP.color.navy}
            />
          </Grid>
        </Grid>

        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FilterAltRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha Desde"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  type="date"
                  label="Fecha Hasta"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={suplidores}
                  loading={cargandoSupl}
                  getOptionLabel={(o) => opcionLabel(o)}
                  value={suplidorSel}
                  onChange={(_, v) => setSuplidorSel(v)}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Suplidor"
                      placeholder="Todos"
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                  onClick={() => buscar()}
                  disabled={cargando || !usuarioLog}
                  sx={{ ...btnPrimarySx, height: 40, px: 3 }}
                >
                  Buscar avances
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    cargarSuplidores();
                    buscar();
                  }}
                  disabled={cargando}
                  sx={btnOutlinedSx}
                >
                  Actualizar
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Avances a suplidor
            </Typography>
            <Typography
              sx={{
                ml: 'auto',
                color: FP.color.textSecondary,
                fontSize: '0.85rem',
                fontWeight: 700,
              }}
            >
              Total: ${fmtMoney(totalLista)}
            </Typography>
          </Box>
          <Box sx={{ height: 480, width: '100%', p: { xs: 1.5, md: 2 } }}>
            <DataGrid
              rows={filas}
              columns={columns}
              disableRowSelectionOnClick
              loading={cargando}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              rowHeight={44}
              sx={fpDataGridSx}
            />
          </Box>
        </Card>
      </Box>

      <AvancesSuplidor
        open={dialogoNuevo}
        onClose={() => setDialogoNuevo(false)}
        onGuardado={buscar}
      />
    </Box>
  );
};

export default MantenimientoAvancesSuplidor;
